/**
 * Filter state of the marketplace store, mirrored from the REAL GAIN website
 * (`real-gain-website/src/features/marketplace/marketplace-filters.ts`): the same
 * URL contract `?scope=&q=&price=`, including the `assetmanager` / `facilitymanager`
 * aliases that links from the REAL GAIN site use.
 */
export type Scope = 'all' | 'assetManagement' | 'facilityManagement';
export type PriceFilter = 'all' | 'free' | 'paid' | 'onRequest';

export const SCOPE_VALUES: Scope[] = ['all', 'assetManagement', 'facilityManagement'];
export const PRICE_VALUES: PriceFilter[] = ['all', 'free', 'paid', 'onRequest'];

export interface StoreFilters {
    scope: Scope;
    q: string;
    price: PriceFilter;
}

/** URL parameters owned by the filters; cleared whenever the user leaves the store. */
export const FILTER_PARAMS = ['scope', 'q', 'price', 'category', 'provider'] as const;

/** Offer types, in the order the category cards and sections use. */
export const CATEGORY_VALUES = ['AgentToolset', 'KnowledgeBase', 'AgenticWorkflow', 'PromptTool'] as const;

export function parseCategory(raw: string | null): string | null {
    return CATEGORY_VALUES.some((type) => type === raw) ? raw : null;
}

/**
 * A "collection" is a filter subpage: scope, price, category or provider is set.
 * Search text alone stays on the store's front page.
 */
export function hasCollection(params: URLSearchParams): boolean {
    return parseScope(params.get('scope')) !== 'all'
        || parsePrice(params.get('price')) !== 'all'
        || parseCategory(params.get('category')) !== null
        || Boolean(params.get('provider'));
}

/** Link target of the store's front page, optionally narrowed to one collection. */
export function storeHref(patch: Record<string, string> = {}): string {
    const params = new URLSearchParams({ panel: 'agent', tab: 'marketplace', ...patch });
    return `/settings?${params.toString()}`;
}

export function parseScope(raw: string | null): Scope {
    const value = (raw ?? '').trim().toLowerCase();
    if (value === 'assetmanagement' || value === 'assetmanager') return 'assetManagement';
    if (value === 'facilitymanagement' || value === 'facilitymanager') return 'facilityManagement';
    return 'all';
}

export function parsePrice(raw: string | null): PriceFilter {
    return PRICE_VALUES.includes(raw as PriceFilter) ? (raw as PriceFilter) : 'all';
}

export function readFilters(params: URLSearchParams): StoreFilters {
    return {
        scope: parseScope(params.get('scope')),
        q: params.get('q') ?? '',
        price: parsePrice(params.get('price')),
    };
}

/** New query string: default values drop out so the URL stays short. */
export function writeFilters(params: URLSearchParams, patch: Partial<StoreFilters>): URLSearchParams {
    const next = new URLSearchParams(params);
    const set = (key: string, value: string | undefined, fallback: string) => {
        if (value === undefined) return;
        if (value === '' || value === fallback) next.delete(key);
        else next.set(key, value);
    };
    set('scope', patch.scope, 'all');
    set('q', patch.q, '');
    set('price', patch.price, 'all');
    return next;
}

/*
 * Scope assignment, taken over from the REAL GAIN website: exclusion lists per
 * audience. An offer in neither list is shown to both. The lists are hard-coded
 * there as well (it names a backend field as the proper home), and
 * 6899c00fcadd92364ebdecf7 sits in both, so that offer shows up under neither scope.
 */
const ASSET_EXCLUSIVE_IDS = new Set([
    '6899c00fcadd92364ebdecf7',
    '6853ebe87061a359781f4f23',
    '6857c8f4d1de6d269a5d0ee3',
    '6899b86ab3058e7e37af5932',
]);
const FACILITY_EXCLUSIVE_IDS = new Set([
    '6826f8bb806731be75f9b057',
    '68987ade2655c9bced9af059',
    '6899c00fcadd92364ebdecf7',
    '6954111fa7b7c17f8ea5c30b',
    '685c269146fc86a4dea12a68',
    '6827054606d35945c21430c0',
    '685cf83204001f7dd51ad492',
    '68806ec7b98ac9f6fb72ddec',
]);

export function matchesScope(offer: { _id?: unknown }, scope: Scope): boolean {
    const id = String(offer._id ?? '').trim();
    if (scope === 'assetManagement') return !FACILITY_EXCLUSIVE_IDS.has(id);
    if (scope === 'facilityManagement') return !ASSET_EXCLUSIVE_IDS.has(id);
    return true;
}

const isFreePlan = (plan: any): boolean =>
    Number(plan?.baseFee ?? 0) === 0
    && (Array.isArray(plan?.billings) ? plan.billings : []).every((billing: any) => Number(billing?.fee ?? 0) === 0);

/** Price categories of an offer, consistent with the price shown on its card. */
export function priceCategories(plans: any[] | undefined) {
    const list = Array.isArray(plans) ? plans : [];
    return {
        free: list.some(isFreePlan),
        paid: list.some((plan) => !isFreePlan(plan)),
        onRequest: list.length === 0,
    };
}

export function matchesPrice(offer: { plans?: any[] }, price: PriceFilter): boolean {
    if (price === 'all') return true;
    return priceCategories(offer.plans)[price];
}
